import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, ChartLine, House, Notebook, User, UsersThree,
  TrendUp, TrendDown, Fire, Scales, Calendar, Plus, Check,
  Trophy, Target, Image as ImageIcon, X, Bell, Warning
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { Progress } from "../components/ui/progress";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart, ReferenceLine, BarChart, Bar } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const ProgressPage = () => {
  const navigate = useNavigate();
  const { user, fetchProfile } = useAuth();
  const [weeklyData, setWeeklyData] = useState(null);
  const [weightChartData, setWeightChartData] = useState(null);
  const [mealHistory, setMealHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [newWeight, setNewWeight] = useState("");
  const [savingWeight, setSavingWeight] = useState(false);
  const [weightFeedback, setWeightFeedback] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showGoalAlert, setShowGoalAlert] = useState(false);
  const [todayProgress, setTodayProgress] = useState(null);

  useEffect(() => {
    fetchData();
    checkGoalAlert();
  }, []);

  const fetchData = async () => {
    try {
      const [weeklyResponse, weightResponse, historyResponse, dashboardResponse] = await Promise.all([
        axios.get(`${API}/progress/weekly`),
        axios.get(`${API}/weight/weekly-chart`),
        axios.get(`${API}/meals/history?days=14`),
        axios.get(`${API}/progress/dashboard`)
      ]);
      setWeeklyData(weeklyResponse.data);
      setWeightChartData(weightResponse.data);
      setMealHistory(historyResponse.data.meals || []);
      setTodayProgress(dashboardResponse.data);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Check if user needs goal alert (after 21:00 and goal not met)
  const checkGoalAlert = () => {
    const now = new Date();
    const hour = now.getHours();
    const alertShown = sessionStorage.getItem('goal_alert_shown_today');
    
    if (hour >= 21 && !alertShown) {
      // Will check after data loads
      setTimeout(() => {
        if (todayProgress?.calories && !todayProgress.calories.goal_achieved) {
          setShowGoalAlert(true);
          sessionStorage.setItem('goal_alert_shown_today', 'true');
        }
      }, 1000);
    }
  };

  useEffect(() => {
    if (todayProgress?.calories && !todayProgress.calories.goal_achieved) {
      const now = new Date();
      const hour = now.getHours();
      const alertShown = sessionStorage.getItem('goal_alert_shown_today');
      
      if (hour >= 21 && !alertShown) {
        setShowGoalAlert(true);
        sessionStorage.setItem('goal_alert_shown_today', 'true');
      }
    }
  }, [todayProgress]);

  const handleWeightSubmit = useCallback(async () => {
    const weight = parseFloat(newWeight.replace(',', '.'));
    
    if (isNaN(weight) || weight < 20 || weight > 300) {
      toast.error("Por favor, insere um peso válido (20-300 kg)");
      return;
    }

    setSavingWeight(true);
    try {
      const response = await axios.post(`${API}/weight`, { weight });
      
      if (response.data.feedback) {
        setWeightFeedback(response.data.feedback);
        toast.success(response.data.feedback.message);
      } else {
        toast.success("Peso registrado com sucesso!");
      }
      
      await fetchData();
      await fetchProfile();
      
      setShowWeightModal(false);
      setNewWeight("");
    } catch (error) {
      console.error('Weight save error:', error);
      toast.error(error.response?.data?.detail || "Erro ao registrar peso");
    } finally {
      setSavingWeight(false);
    }
  }, [newWeight, fetchProfile]);

  const isSubscribed = user?.subscription_status === 'ativo' || user?.subscription_status === 'trial';

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <header className="bg-primary text-white p-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="p-2">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Progresso Semanal</h1>
        </header>
        <main className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-bold mb-2">Funcionalidade Premium</h2>
          <p className="text-muted-foreground text-center mb-6">
            Os relatórios semanais estão disponíveis apenas para assinantes.
          </p>
          <button 
            onClick={() => navigate('/payment')}
            className="btn-primary"
          >
            Ver Planos
          </button>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-primary font-bold text-xl">Nzadi Fit</div>
        </div>
      </div>
    );
  }

  const { summary, daily_data, period } = weeklyData || {};
  const { chart_data, goal, target_weight, current_weight } = weightChartData || {};

  // Prepare calories chart data with goal line
  const caloriesChartData = Object.entries(daily_data?.calories || {}).map(([date, calories]) => ({
    date: new Date(date).toLocaleDateString('pt', { weekday: 'short' }),
    fullDate: date,
    calories,
    goal: user?.daily_calories_target || todayProgress?.calories?.target || 2000
  }));

  // Calculate weekly summary stats
  const daysWithMeals = Object.values(daily_data?.calories || {}).filter(c => c > 0).length;
  const daysGoalMet = caloriesChartData.filter(d => d.calories >= d.goal * 0.9).length;
  const totalDays = 7;

  // Prepare weight chart data with weekday labels
  const weightChartFormatted = (chart_data || []).map(item => ({
    ...item,
    displayWeight: item.weight || null
  }));

  // Get min/max for chart domain
  const weightsWithData = weightChartFormatted.filter(w => w.weight !== null);
  const minWeight = weightsWithData.length > 0 ? Math.min(...weightsWithData.map(w => w.weight)) - 1 : 60;
  const maxWeight = weightsWithData.length > 0 ? Math.max(...weightsWithData.map(w => w.weight)) + 1 : 80;

  // Group meals by date for timeline
  const mealsByDate = mealHistory.reduce((acc, meal) => {
    const date = new Date(meal.created_at).toLocaleDateString('pt-BR');
    if (!acc[date]) acc[date] = [];
    acc[date].push(meal);
    return acc;
  }, {});

  // Meals with images
  const mealsWithImages = mealHistory.filter(m => m.image_url);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Progresso Semanal</h1>
        </div>
        <button
          onClick={() => setShowWeightModal(true)}
          className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full flex items-center gap-2 transition-colors"
          data-testid="log-weight-button"
        >
          <Scales size={20} weight="bold" />
          <span className="text-sm font-medium">Registrar Peso</span>
        </button>
      </header>

      <main className="p-4 space-y-4">
        {/* Period */}
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Calendar size={16} />
          <span className="text-sm">
            {period?.start && new Date(period.start).toLocaleDateString('pt', { day: '2-digit', month: 'short' })} - {period?.end && new Date(period.end).toLocaleDateString('pt', { day: '2-digit', month: 'short' })}
          </span>
        </div>

        {/* Weekly Summary Banner - "Balanço da Jornada" */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-5 ${
            daysGoalMet >= 6 
              ? 'bg-gradient-to-r from-amber-400 to-orange-500' 
              : daysGoalMet >= 4 
                ? 'bg-gradient-to-r from-primary to-primary/80'
                : 'bg-gradient-to-r from-slate-600 to-slate-700'
          } text-white`}
          data-testid="weekly-summary-banner"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={24} weight="fill" className={daysGoalMet >= 6 ? 'text-yellow-200' : 'text-white/80'} />
                <span className="text-sm font-medium text-white/80">Balanço da Jornada</span>
              </div>
              <p className="text-2xl font-bold">
                {daysGoalMet}/{totalDays} dias de meta batida!
              </p>
              <p className="text-sm text-white/80 mt-1">
                {daysGoalMet >= 6 
                  ? '🌟 Semana incrível! Estás a arrasar!' 
                  : daysGoalMet >= 4 
                    ? '💪 Bom progresso! Continua assim!'
                    : '🎯 Próxima semana será melhor!'}
              </p>
            </div>
            <div className="text-5xl">
              {daysGoalMet >= 6 ? '🏆' : daysGoalMet >= 4 ? '⭐' : '🎯'}
            </div>
          </div>
        </motion.div>

        {/* Goal Alert Banner (21:00 check) */}
        <AnimatePresence>
          {showGoalAlert && todayProgress?.calories && !todayProgress.calories.goal_achieved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4"
              data-testid="goal-alert-banner"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bell size={24} className="text-amber-600" weight="fill" />
                  </div>
                  <div>
                    <p className="font-bold text-amber-800">A tua meta de hoje está à espera! 🔥</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Faltam <strong>{Math.max(0, todayProgress.calories.target - todayProgress.calories.consumed)} kcal</strong> para bateres a meta de hoje.
                    </p>
                    <button 
                      onClick={() => navigate('/dashboard')}
                      className="mt-2 text-sm font-semibold text-amber-700 hover:text-amber-900"
                    >
                      Registrar refeição →
                    </button>
                  </div>
                </div>
                <button 
                  onClick={() => setShowGoalAlert(false)}
                  className="text-amber-500 hover:text-amber-700"
                >
                  <X size={20} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Weight Feedback Banner */}
        <AnimatePresence>
          {weightFeedback && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`rounded-2xl p-4 text-center ${
                weightFeedback.type === 'success' 
                  ? 'bg-primary text-white' 
                  : 'bg-zest/10 text-foreground'
              }`}
            >
              <p className="text-lg font-bold">{weightFeedback.message}</p>
              <p className="text-sm mt-1 opacity-80">
                {weightFeedback.weight_change > 0 ? '+' : ''}{weightFeedback.weight_change.toFixed(2)} kg desde ontem
              </p>
              <button 
                onClick={() => setWeightFeedback(null)}
                className="mt-2 text-sm underline"
              >
                Fechar
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Current Weight Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary to-primary/80 text-white rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Peso Atual</p>
              <p className="text-4xl font-bold font-mono">{current_weight?.toFixed(1) || '--'} kg</p>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">Meta</p>
              <p className="text-2xl font-bold font-mono">{target_weight?.toFixed(1) || '--'} kg</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm text-white/80 mb-1">
              <span>Progresso</span>
              <span>
                {goal === 'perder' && current_weight && target_weight 
                  ? `Faltam ${Math.max(0, current_weight - target_weight).toFixed(1)} kg`
                  : goal === 'ganhar' && current_weight && target_weight
                    ? `Faltam ${Math.max(0, target_weight - current_weight).toFixed(1)} kg`
                    : 'Mantendo'}
              </span>
            </div>
            <Progress 
              value={
                goal === 'perder' && current_weight && target_weight
                  ? Math.min(100, Math.max(0, ((current_weight - target_weight) / (current_weight - target_weight + 5)) * 100))
                  : goal === 'ganhar' && current_weight && target_weight
                    ? Math.min(100, Math.max(0, ((current_weight - (target_weight - 5)) / 5) * 100))
                    : 100
              } 
              className="h-2 bg-white/30" 
            />
          </div>
        </motion.div>

        {/* Weekly Weight Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-4 shadow-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-foreground flex items-center gap-2">
              <Scales size={20} className="text-primary" />
              Evolução do Peso
            </h3>
            <span className="text-xs text-muted-foreground">Últimos 7 dias</span>
          </div>
          
          {weightsWithData.length > 0 ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weightChartFormatted} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <XAxis 
                    dataKey="weekday" 
                    tick={{ fontSize: 11 }} 
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    domain={[minWeight, maxWeight]} 
                    tick={{ fontSize: 10 }} 
                    axisLine={false}
                    tickLine={false}
                    width={35}
                  />
                  <Tooltip 
                    formatter={(value) => value ? [`${value.toFixed(1)} kg`, 'Peso'] : ['Sem dados', '']}
                    labelFormatter={(label) => label}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  {target_weight && (
                    <ReferenceLine 
                      y={target_weight} 
                      stroke="#2E7D32" 
                      strokeDasharray="5 5" 
                      label={{ value: 'Meta', position: 'right', fontSize: 10, fill: '#2E7D32' }}
                    />
                  )}
                  <Line 
                    type="monotone" 
                    dataKey="displayWeight" 
                    stroke="#FF6F00" 
                    strokeWidth={3}
                    dot={{ fill: '#FF6F00', strokeWidth: 0, r: 5 }}
                    connectNulls={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-52 flex flex-col items-center justify-center text-muted-foreground">
              <Scales size={48} weight="duotone" className="mb-3 text-muted-foreground/50" />
              <p>Nenhum peso registrado</p>
              <button
                onClick={() => setShowWeightModal(true)}
                className="mt-3 text-primary font-semibold hover:underline"
              >
                Registrar primeiro peso
              </button>
            </div>
          )}
        </motion.div>

        {/* Calories per Day Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 shadow-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-foreground flex items-center gap-2">
              <Fire size={20} className="text-orange-500" />
              Calorias por Dia
            </h3>
            <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded-full">
              Meta: {user?.daily_calories_target || todayProgress?.calories?.target || 2000} kcal
            </span>
          </div>
          
          {caloriesChartData.length > 0 ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={caloriesChartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    axisLine={false} 
                    tickLine={false} 
                  />
                  <YAxis hide />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'calories') return [`${value} kcal`, 'Consumido'];
                      return [value, name];
                    }}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                  <ReferenceLine 
                    y={user?.daily_calories_target || todayProgress?.calories?.target || 2000} 
                    stroke="#2E7D32" 
                    strokeDasharray="5 5"
                  />
                  <Bar 
                    dataKey="calories" 
                    fill="#2E7D32" 
                    radius={[8, 8, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-52 flex flex-col items-center justify-center text-muted-foreground">
              <Fire size={48} weight="duotone" className="mb-3 text-muted-foreground/50" />
              <p>Nenhuma refeição registrada</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-3 text-primary font-semibold hover:underline"
              >
                Registrar primeira refeição
              </button>
            </div>
          )}
        </motion.div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-4 shadow-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <Fire size={20} className="text-zest" weight="duotone" />
              <span className="text-sm text-muted-foreground">Média Calorias</span>
            </div>
            <p className="text-2xl font-bold font-mono text-foreground">{summary?.avg_daily_calories || 0}</p>
            <p className="text-xs text-muted-foreground">kcal/dia</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-4 shadow-card"
          >
            <div className="flex items-center gap-2 mb-2">
              {summary?.weight_change < 0 ? (
                <TrendDown size={20} className="text-primary" weight="duotone" />
              ) : summary?.weight_change > 0 ? (
                <TrendUp size={20} className="text-zest" weight="duotone" />
              ) : (
                <ChartLine size={20} className="text-muted-foreground" weight="duotone" />
              )}
              <span className="text-sm text-muted-foreground">Variação</span>
            </div>
            <p className={`text-2xl font-bold font-mono ${
              goal === 'perder' 
                ? summary?.weight_change < 0 ? 'text-primary' : summary?.weight_change > 0.3 ? 'text-destructive' : 'text-foreground'
                : goal === 'ganhar'
                  ? summary?.weight_change > 0 ? 'text-primary' : summary?.weight_change < -0.3 ? 'text-destructive' : 'text-foreground'
                  : Math.abs(summary?.weight_change || 0) <= 0.3 ? 'text-primary' : 'text-zest'
            }`}>
              {summary?.weight_change > 0 ? '+' : ''}{(summary?.weight_change || 0).toFixed(2)} kg
            </p>
            <p className="text-xs text-muted-foreground">esta semana</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-4 shadow-card col-span-2"
          >
            <div className="flex items-center gap-2 mb-2">
              <Notebook size={20} className="text-primary" weight="duotone" />
              <span className="text-sm text-muted-foreground">Total de Refeições</span>
            </div>
            <p className="text-2xl font-bold font-mono text-foreground">{summary?.total_meals || 0}</p>
            <p className="text-xs text-muted-foreground">refeições registradas esta semana</p>
          </motion.div>
        </div>

        {/* Meal Photo Gallery */}
        {mealsWithImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-4 shadow-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground flex items-center gap-2">
                <ImageIcon size={20} className="text-primary" />
                Galeria de Refeições
              </h3>
              <span className="text-xs text-muted-foreground">{mealsWithImages.length} fotos</span>
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {mealsWithImages.slice(0, 9).map((meal, index) => (
                <motion.div
                  key={meal.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
                  onClick={() => setSelectedImage(meal)}
                >
                  <img 
                    src={meal.image_url} 
                    alt={meal.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-2 left-2 right-2">
                      <p className="text-white text-xs font-medium truncate">{meal.name}</p>
                      <p className="text-white/80 text-xs">{meal.calories} kcal</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            {mealsWithImages.length > 9 && (
              <p className="text-center text-sm text-muted-foreground mt-3">
                +{mealsWithImages.length - 9} mais fotos
              </p>
            )}
          </motion.div>
        )}

        {/* Meal Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl p-4 shadow-card"
        >
          <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            Histórico de Refeições
          </h3>
          
          {Object.keys(mealsByDate).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(mealsByDate).slice(0, 5).map(([date, meals]) => (
                <div key={date} className="border-l-2 border-primary/30 pl-4">
                  <p className="text-sm font-semibold text-foreground mb-2">{date}</p>
                  <div className="space-y-2">
                    {meals.map((meal) => (
                      <div 
                        key={meal.id} 
                        className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
                        onClick={() => meal.image_url && setSelectedImage(meal)}
                      >
                        {meal.image_url ? (
                          <img 
                            src={meal.image_url} 
                            alt={meal.name}
                            className="w-12 h-12 rounded-lg object-cover cursor-pointer"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            <span className="text-xl">🍽️</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">{meal.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(meal.created_at).toLocaleTimeString('pt', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm text-foreground">{meal.calories}</p>
                          <p className="text-xs text-muted-foreground">kcal</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Notebook size={48} className="mx-auto mb-3 opacity-50" />
              <p>Nenhuma refeição registrada</p>
            </div>
          )}
        </motion.div>
      </main>

      {/* Weight Log Modal */}
      <Dialog open={showWeightModal} onOpenChange={setShowWeightModal}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-center">Registrar Peso</DialogTitle>
          </DialogHeader>
          
          <div className="py-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scales size={32} className="text-primary" weight="duotone" />
              </div>
              <p className="text-muted-foreground text-sm">Insere o teu peso atual em kg</p>
            </div>

            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                placeholder="70,5"
                value={newWeight}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.,]/g, '');
                  setNewWeight(value);
                }}
                className="w-full text-center text-4xl font-bold font-mono bg-muted/50 border-2 border-transparent focus:border-primary rounded-2xl py-6 outline-none transition-all"
                data-testid="weight-input"
                autoFocus
              />
              <span className="absolute right-6 top-1/2 -translate-y-1/2 text-muted-foreground text-xl font-medium">kg</span>
            </div>

            <p className="text-center text-xs text-muted-foreground mt-3">
              Usa vírgula ou ponto para decimais (ex: 70,5 ou 70.5)
            </p>
          </div>

          <DialogFooter className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowWeightModal(false)}
              className="flex-1 rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleWeightSubmit}
              disabled={savingWeight || !newWeight}
              className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
            >
              {savingWeight ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Check size={20} className="mr-2" />
                  Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="bg-white rounded-3xl border-none max-w-md mx-auto p-0 overflow-hidden">
          {selectedImage && (
            <>
              <div className="relative">
                <img 
                  src={selectedImage.image_url} 
                  alt={selectedImage.name}
                  className="w-full aspect-square object-cover"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg">{selectedImage.name}</h3>
                <p className="text-muted-foreground text-sm">
                  {new Date(selectedImage.created_at).toLocaleDateString('pt', { 
                    weekday: 'long', 
                    day: '2-digit', 
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <div className="flex gap-4 mt-3">
                  <div className="flex-1 bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-primary">{selectedImage.calories}</p>
                    <p className="text-xs text-muted-foreground">kcal</p>
                  </div>
                  <div className="flex-1 bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedImage.protein}g</p>
                    <p className="text-xs text-muted-foreground">proteína</p>
                  </div>
                  <div className="flex-1 bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">{selectedImage.carbs}g</p>
                    <p className="text-xs text-muted-foreground">carbs</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-muted px-6 py-3">
        <div className="flex items-center justify-around max-w-md mx-auto">
          <button onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 text-muted-foreground">
            <House size={24} weight="duotone" />
            <span className="text-xs">Início</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-primary">
            <ChartLine size={24} weight="duotone" />
            <span className="text-xs font-medium">Progresso</span>
          </button>
          <button onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 text-muted-foreground">
            <User size={24} weight="duotone" />
            <span className="text-xs">Perfil</span>
          </button>
          <button onClick={() => navigate('/affiliates')} className="flex flex-col items-center gap-1 text-muted-foreground">
            <UsersThree size={24} weight="duotone" />
            <span className="text-xs">Afiliados</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default ProgressPage;
